import React, { useMemo } from "react";
import { useOrganizationVendors, usePrimaryOrganizationId } from "../../../../hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useDataMap } from "../../../../../common/hooks/api/useDataMap";
import { useSurvey } from "../../../../../common/hooks/api/useSurvey";
import { useCollectionGroups } from "../../../../hooks/useCollectionGroups";
import { useReceivedForCollectionGroups } from "../../../../hooks/useReceivedForCollectionGroups";
import {
  EMPLOYEE_OTHER_EXCLUDED_CATEGORIES,
  employeeSourcesSurvey,
} from "../../../../surveys/employeeSourcesSurvey";
import { isFinished } from "../../../../surveys/survey";
import {
  VENDOR_CATEGORY_APPLICANT_TRACKING,
  VENDOR_CATEGORY_RECRUITMENT_SERVICE,
  VENDOR_CATEGORY_DATA_BROKER,
  VENDOR_CATEGORY_BACKGROUND_CHECK_PROVIDER,
} from "../../../../types/Vendor";
import { lexicalJoin } from "../../../../util/text";
import { useUpdateDataSources } from "../../../get-compliant/data-map/ConsumerDataSourcesPage";
import { SurveyGateButton } from "../../../../components/organisms/survey/SurveyGateButton";
import { SurveyRenderer } from "../../../../components/get-compliant/survey/SurveyRenderer";
import { SurveyQuestionContainer } from "../../../../components/get-compliant/survey/SurveyBooleanQuestion";
import {
  QuestionContainer,
  QuestionLabel,
} from "../../../../components/get-compliant/survey/QuestionContainer";
import { Stack } from "@mui/material";
import { DataSourceReceiptCards } from "../../../../components/organisms/DataSourceReceiptCards";
import { useHistory } from "react-router";
import { Routes } from "../../../../root/routes";
import { PageLoading } from "../../../../components";
import { EmployeePageWrapper } from "./EmployeePageWrapper";

const EMPLOYEE_SOURCES_SURVEY_NAME = "employee-sources-survey";

export function EmployeeSourcesPage() {
  const history = useHistory();

  const orgId = usePrimaryOrganizationId();

  const {
    data: survey,
    remote: remoteSurvey,
    answers,
    setAnswer,
    request: surveyRequest,
  } = useSurvey(orgId, EMPLOYEE_SOURCES_SURVEY_NAME);
  const [recipients, recipientsRequest] = useOrganizationVendors(
    orgId,
    undefined,
    undefined,
    undefined,
    ["EMPLOYEE"],
  );
  const [employmentGroups, groupsRequest] = useCollectionGroups(orgId, ["EMPLOYMENT"]);
  const receivedData = useReceivedForCollectionGroups(orgId, employmentGroups, ["EMPLOYEE"]);
  const updateDataSources = useUpdateDataSources(orgId, { collectionContext: ["EMPLOYEE"] });
  const queryClient = useQueryClient();
  const { data: dataMap } = useDataMap(orgId);

  const hasApplicantTracking = useMemo(
    () =>
      updateDataSources.data.sources.some((s) => {
        return (
          s.category == VENDOR_CATEGORY_APPLICANT_TRACKING ||
          s.category == VENDOR_CATEGORY_RECRUITMENT_SERVICE
        );
      }),
    [updateDataSources.data.sources],
  );
  const hasDataBroker = useMemo(
    () => updateDataSources.data.sources.some((s) => s.category == VENDOR_CATEGORY_DATA_BROKER),
    [updateDataSources.data.sources],
  );
  const hasBackgroundCheck = useMemo(
    () =>
      updateDataSources.data.sources.some(
        (s) => s.category == VENDOR_CATEGORY_BACKGROUND_CHECK_PROVIDER,
      ),
    [updateDataSources.data.sources],
  );
  const hasOtherSource = useMemo(
    () =>
      updateDataSources.data.sources.some(
        (s) => !EMPLOYEE_OTHER_EXCLUDED_CATEGORIES.includes(s.category as any),
      ),
    [updateDataSources.data.sources],
  );

  const groupLabel = employmentGroups
    ? lexicalJoin(employmentGroups.map((g) => g.name))
    : "your employment groups";

  const surveyDefinition = useMemo(
    () =>
      employeeSourcesSurvey(
        answers,
        groupLabel,
        updateDataSources.data.catalog,
        receivedData.sources,
        recipients,
        {
          handleAddVendor: updateDataSources.actions.handleAddVendor,
          handleAddCustomVendor: updateDataSources.actions.handleAddCustomVendor,
          handleRemoveVendor: updateDataSources.actions.handleRemoveVendor,
        },
      ),
    [
      answers,
      groupLabel,
      updateDataSources.data.catalog,
      receivedData.sources,
      recipients,
      updateDataSources.actions,
    ],
  );
  const surveyFinished = isFinished(surveyDefinition, answers);

  const handleSetAnswer = async (slug: string, answer: string, question?: string) => {
    await setAnswer(slug, answer, question);
    if (slug == "worker-directly") {
      queryClient.invalidateQueries(["orgDataSources", orgId]);
    }
  };

  const dataBrokersDone = answers["data-brokers"] === "false" || hasDataBroker;
  const applicantTrackingDone =
    answers["applicant-tracking-systems"] === "false" || hasApplicantTracking;
  const backgroundCheckDone =
    answers["background-check-providers"] === "false" || hasBackgroundCheck;
  const otherDone = answers["other-sources"] === "false" || hasOtherSource;

  const hasDataSource = updateDataSources.data.sources.length > 0;
  const isDone =
    surveyFinished &&
    applicantTrackingDone &&
    dataBrokersDone &&
    backgroundCheckDone &&
    otherDone &&
    receivedData.pendingComplete &&
    hasDataSource;

  if (surveyRequest.running || groupsRequest.running || recipientsRequest.running) {
    return <PageLoading />;
  }

  return (
    <EmployeePageWrapper
      selectedTabKey="employee-sources"
      nextOnClick={() => history.push(Routes.hrCenter.hrDataMap.employeeDataAccess)}
      nextDisabled={!isDone}
    >
      <div className="tw-boundary" style={{ marginBottom: "60px", maxWidth: "800px" }}>
        <p>
          In this step, you'll identify the sources of personal data for your {groupLabel}. As with
          consumers, the individual themselves is generally the source of most data. But your
          business may also collect data from other sources too.
        </p>
      </div>

      <div style={{ maxWidth: "800px" }}>
        <SurveyGateButton
          slug="sources-acknowledgement"
          survey={answers}
          updateResponse={setAnswer}
          label="Get Started"
        >
          <SurveyRenderer
            definition={surveyDefinition}
            survey={survey}
            remote={remoteSurvey}
            setAnswer={handleSetAnswer}
          />

          {surveyFinished && (
            <div className="my-xl">
              <SurveyQuestionContainer>
                <div className="survey--question survey-question--top-margin">
                  <QuestionContainer>
                    <QuestionLabel
                      label="Almost Done! Finally, let us know what personal information you receive from each source."
                      helpText="The data you previously indicated you collect is listed below each source. Select which types of data are collected from each of your sources. If you need to remove a source, update your answer above. "
                      recommendation={
                        !receivedData || receivedData.sources.length == 0
                          ? "You are required to add at least one data source to continue. Change an answer to 'yes' above to add a source."
                          : null
                      }
                    />

                    <Stack spacing={3} className="my-lg">
                      <DataSourceReceiptCards
                        dataMap={dataMap}
                        receivedData={receivedData}
                        groups={employmentGroups}
                        consumerDirectlyName="Worker"
                        showPendingSources={true}
                      />
                    </Stack>
                  </QuestionContainer>
                </div>
              </SurveyQuestionContainer>
            </div>
          )}
        </SurveyGateButton>
      </div>
    </EmployeePageWrapper>
  );
}
